import uuid from 'node-uuid';
import lodash from 'lodash';

import { queryGraphQL, mutateGraphQL } from 'complionUtil/query';
import { toMutation, toQuery, nodeWithArgs } from 'complionUtil/queryHelper';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { binderInstitutionIdSelector } from 'common/selectors';

import { loadStudyDelegationsV2 } from './studyDelegationsV2';
import { loadSignatureExpectationsV2 } from './studySignaturesV2';
import { retrieveStudyAuditLogData } from './studyAuditLogV2';
export const LOAD_STUDY_TASKS_V2 = 'LOAD_STUDY_TASKS_V2';
export const LOAD_STUDY_TASKS_FOR_EXPORT_V2 = 'LOAD_STUDY_TASKS_FOR_EXPORT_V2';
export const CREATE_STUDY_TASK_V2 = 'CREATE_STUDY_TASK_V2';
export const REMOVE_STUDY_TASK_V2 = 'REMOVE_STUDY_TASK_V2';
export const IMPORT_STUDY_TEMPLATE_V2 = 'IMPORT_STUDY_TEMPLATE_V2';
export const SAVE_STUDY_TASK_V2 = 'SAVE_STUDY_TASK_V2';
export const REMOVE_STUDY_TASKS_V2 = 'REMOVE_STUDY_TASKS_V2';

export function loadStudyTasksV2(binderId, studyId) {
    return async (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        const query = nodeWithArgs(
            'studyTasksV2',
            {
                binderId,
                institutionId,
                studyId,
            },
            ['id', 'taskName', 'taskIndex']
        );

        return queryGraphQL(toQuery(query)).then((results) => {
            const { studyTasksV2 } = results.data;
            dispatch({
                type: LOAD_STUDY_TASKS_V2,
                payload: studyTasksV2,
            });
        });
    };
}

export function loadStudyTasksForExportV2(binderId, studyId) {
    return async (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        const query = nodeWithArgs(
            'studyTasksV2',
            {
                binderId,
                institutionId,
                studyId,
                includeDeleted: true
            },
            ['id', 'taskName']
        );

        return queryGraphQL(toQuery(query)).then((results) => {
            const { studyTasksV2 } = results.data;
            dispatch({
                type: LOAD_STUDY_TASKS_FOR_EXPORT_V2,
                payload: studyTasksV2,
            });
        });
    };
}

export function createStudyTaskV2(binderId, studyId, taskName) {
    const id = uuid.v4();

    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        const tasks = lodash.get(state, 'doaStudyTasks', []);
        let taskIndex = 1;
        if (tasks.length !== 0) {
            const taskIndexes = tasks.map((task) => task.taskIndex);
            const largestCurrentTaskIndex = Math.max(...taskIndexes);
            taskIndex = largestCurrentTaskIndex + 1;
        }

        const query = nodeWithArgs(
            'createStudyTaskV2',
            {
                id,
                binderId,
                studyId,
                institutionId,
                taskName,
                taskIndex,
            },
            ['id', 'taskName', 'taskIndex']
        );
        return mutateGraphQL(toMutation(query)).then((results) => {
            if (results && results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to add task to study'));
            } else {
                dispatch({
                    type: CREATE_STUDY_TASK_V2,
                    payload: results.data.createStudyTaskV2,
                });
            }
        });
    };
}

export function saveStudyTaskV2(newListTask, binderId, studyId) {
    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        const query = nodeWithArgs(
            'saveStudyTasksV2',
            {
                tasks: newListTask,
                binderId,
                studyId,
                institutionId,
            },
            ['id', 'taskName', 'taskIndex']
        );
        return mutateGraphQL(toMutation(query)).then((results) => {
            if (results && results.errors) {
                let errorObj = lodash.first(results.errors);
                if (errorObj && errorObj.message) {
                    dispatch(addApplicationMessage('error', errorObj.message));
                } else {
                    dispatch(addApplicationMessage('error', 'Failed to add task to study'));
                }
            } else {
                dispatch({
                    type: SAVE_STUDY_TASK_V2,
                    payload: {
                        doaStudyTasks: results.data.saveStudyTasksV2,
                    },
                });
            }
        });
    };
}

export function removeStudyTaskV2(taskId, binderId, studyId, tasksToBeUpdated) {
    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        const mutation = toMutation(
            nodeWithArgs(
                'removeStudyTaskV2',
                {
                    id: taskId,
                    binderId,
                    studyId,
                    institutionId,
                    input: tasksToBeUpdated,
                },
                ['taskName', 'taskIndex', 'id']
            )
        );

        return mutateGraphQL(mutation).then((results) => {
            if (results.errors) {
                dispatch(
                    addApplicationMessage(
                        'error',
                        'An error occurred when deleting the study task.'
                    )
                );
            } else {
                dispatch({
                    type: REMOVE_STUDY_TASK_V2,
                    payload: {
                        doaStudyTasks: results.data.removeStudyTaskV2,
                    },
                });
            }
        });
    };
}

export function removeStudyTasksV2(binderId, studyId, tasks, delegations, signatureExpectations, defaultSignatureExpectations, onSuccess) {
    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        const mutation = toMutation(
            nodeWithArgs(
                'removeStudyTasksV2',
                {
                    binderId,
                    studyId,
                    institutionId,
                    tasks,
                    delegations,
                    signatureExpectations,
                    defaultSignatureExpectations
                },
                ['id', 'taskName', 'taskIndex']
            )
        );

        return mutateGraphQL(mutation).then((results) => {
            if (results.errors) {
                dispatch(
                    addApplicationMessage(
                        'error',
                        'An error occurred when deleting the study tasks.'
                    )
                );
            } else {
                dispatch({
                    type: REMOVE_STUDY_TASKS_V2,
                    payload: {
                        doaStudyTasks: results.data.removeStudyTasksV2,
                    },
                }).then(dispatch(loadStudyDelegationsV2(binderId, studyId)))
                    .then(dispatch(loadSignatureExpectationsV2(binderId, studyId)))
                    .then(dispatch(retrieveStudyAuditLogData(binderId, studyId)))
                    .then(() => onSuccess && onSuccess());
            }
        });
    };
}

export function importStudyTemplateV2(templateName, binderId, studyId, institutionId) {
    return (dispatch, getState) => {
        const mutation = nodeWithArgs(
            'importStudyTemplateV2',
            {
                templateName,
                binderId,
                studyId,
                institutionId,
            },
            ['id', 'taskName', 'taskIndex']
        );

        return mutateGraphQL(toMutation(mutation)).then((results) => {
            if (results && results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to import study key template'));
            } else {
                dispatch({
                    type: IMPORT_STUDY_TEMPLATE_V2,
                    payload: results.data.importStudyTemplateV2,
                });
            }
        });
    };
}
